import React, { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { isEmpty, cloneDeep } from 'lodash';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import BtnAddWithoutToolTip from '../../Components/Utility/FilterAction/BtnAddWIthoutToolTip';
import BtnDelete from '../../Components/Utility/FilterAction/BtnDelete';
import AddEditRow from './Modals/AddEditRow';
import * as action from './store/OprationalCP.action';
import MovingCards from './MovingCards';
import { useComponent } from '../UserSetting/UsersLibrary/UserRights';

function RowSection({
  isProductArchived,
  addRow,
  deleteRow,
  editState,
  handleSelectAllRows,
  onSelectCheckBox,
  handleEdit,
  closeRowModal,
  isName,
  setAddEditState,
}) {
  const {
    UserDetails: { isSupplierLoggedIn },
    Users: { logInRights },
  } = useSelector((reducer) => reducer);
  const { CheckActionPermission } = useComponent();
  const dispatch = useDispatch();
  const { id } = useParams();
  const { t } = useTranslation('common');

  const OperationalAddEditAccess = CheckActionPermission(logInRights, 'criticalPath', 'operational', 'addEdit');
  const OperationalDeleteAccess = CheckActionPermission(logInRights, 'criticalPath', 'operational', 'delete');

  function swapArrayLocs(arr, index1, index2) {
    const temp = arr[index1];
    arr[index1] = arr[index2];
    arr[index2] = temp;
    return arr;
  }

  const moveRow = async (movedCardIndex, toCardIndex) => {
    const currList = await swapArrayLocs(editState.Rows, movedCardIndex, toCardIndex);
    setAddEditState((prevCards) => ({
      ...prevCards,
      Rows: currList,
      isDragging: true,
    }));
  };

  const manualRemoval = (ele) => {
    ele.manualType = ele?.manul_type;
    if (['Checkbox', 'Dropdown', 'Multi_select_Dropdown', 'Radio_button'].includes(ele?.manul_type?.key)) {
      delete ele?.labelColoursOptions;
      delete ele?.statusColoursOptions;
      delete ele?.isStatusEditTrue;
      delete ele?.colourCoding;
      delete ele?.status;
      delete ele?.characterLimit;
    } else if (ele?.manul_type?.key === 'Label') {
      delete ele?.options;
      delete ele?.colourCoding;
      delete ele?.statusColoursOptions;
      delete ele?.isStatusEditTrue;
      delete ele?.status;
      delete ele?.characterLimit;
    } else if (ele?.manul_type?.key === 'Status') {
      ele.status = ele?.statusColoursOptions.find((colSt) => colSt?.colourHexCode === ele?.status?.colourHexCode);
      delete ele?.options;
      delete ele?.colourCoding;
      delete ele?.labelColoursOptions;
      delete ele?.isStatusEditTrue;
      delete ele?.characterLimit;
    } else if (ele?.manul_type?.key === 'Textbox') {
      delete ele?.status;
      delete ele?.options;
      delete ele?.colourCoding;
      delete ele?.labelColoursOptions;
      delete ele?.isStatusEditTrue;
      delete ele?.statusColoursOptions;
    } else {
      delete ele?.options;
      delete ele?.labelColoursOptions;
      delete ele?.statusColoursOptions;
      delete ele?.isStatusEditTrue;
      delete ele?.status;
      delete ele?.characterLimit;
    }
  };

  const fromObjectValue = (ele) => {
    return typeof ele === 'object' ? ele?.value : ele;
  };

  const activeInactiveClicked = async (i) => {
    const currentRows = [...editState.Rows];
    const updatedarr = cloneDeep(currentRows);
    updatedarr[i].isActive = !currentRows[i].isActive;
    const updatedArray = await updatedarr.map((ele) => {
      delete ele?.hasError;
      delete ele?.nameCount;
      ele.operationalCPId = id;
      if (ele.source?.subLocation) {
        ele.source.subLocation = fromObjectValue(ele.source?.subLocation);
      }
      if (ele.source?.locationType) {
        ele.source.locationType = fromObjectValue(ele.source?.locationType);
      }
      if (ele.source?.subType) {
        ele.source.subType = fromObjectValue(ele.source?.subType);
      }
      if (ele.source?.section) {
        ele.source.section = fromObjectValue(ele.source?.section);
      }
      if (ele.rowType === 'manual') {
        manualRemoval(ele);
        delete ele?.manul_type;
        delete ele?.source;
        delete ele?.subType;
        delete ele?.locationType;
      }
      ele.sequence = ele.sequence ? ele?.sequence : sequenceChanger(ele);
      return ele;
    });
    const insertObj = {
      rows: [{ ...updatedArray[i] }],
      _id: updatedArray[i]?._id,
      fromArchive: true,
      isArchived: updatedArray[i].isActive,
    };

    const res = await dispatch(action.addRowsToCard(insertObj, t));
    if (res) {
      setAddEditState((oldState) => ({ ...oldState, Rows: updatedArray }));
    }
  };

  const sequenceChanger = (ele) => {
    return editState?.Rows.findIndex((e) => e?._id === ele?._id) !== -1 ? editState?.Rows.findIndex((e) => e?._id === ele?._id) : editState?.Rows.length;
  };

  const renderTableRow = useCallback(
    (rowItem, rowIndex) => {
      return (
        <MovingCards
          id={rowItem?._id}
          isProductArchived={isProductArchived}
          rowItem={rowItem}
          onSelectCheckBox={onSelectCheckBox}
          editState={editState}
          handleEdit={handleEdit}
          activeInactiveClicked={activeInactiveClicked}
          deleteRow={deleteRow}
          index={rowIndex}
          moveRow={moveRow}
          isName={isName}
        />
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editState?.Rows, editState?.selectedRows],
  );

  return (
    <>
      <div className="mt-2">
        <div className="d-flex align-items-center mb-2 ">
          {t('OPERATIONAL_CP.ROW_SECTION.ROWS')}
          {OperationalAddEditAccess && (
            <div className="btn-outline-container ml-1">
              <BtnAddWithoutToolTip onBtnClick={addRow} forDisable={isProductArchived} addEditAccess />
            </div>
          )}
          {OperationalDeleteAccess && (
            <div className="btn-outline-container">
              <BtnDelete multiDelete={() => deleteRow(null)} isDisabled={isEmpty(editState?.selectedRows) || isProductArchived} />
            </div>
          )}
        </div>
        <div className="table-responsive">
          <table className="table  xs-layout-fixed custom-table  m-0">
            <thead>
              <tr>
                {!isSupplierLoggedIn && (OperationalAddEditAccess || OperationalDeleteAccess) && (
                  <th className="w-80px w-xs-40px">
                    <div className="checkbox-label no-label margin-auto">
                      <input
                        type="checkbox"
                        onChange={handleSelectAllRows}
                        name="check_all_rows"
                        id="check_all_rows"
                        checked={editState.selectAllRows}
                        disabled={isEmpty(editState.Rows) || isProductArchived || isName}
                      />
                      <label htmlFor="check_all_rows" className="chklabel">
                        <span />
                      </label>
                    </div>
                  </th>
                )}
                <th className="w-400px w-xs-150px">
                  <span className="cursor-pointer">{t('OPERATIONAL_CP.ROW_SECTION.TASK_NAME')}</span>
                </th>
                <th className="w-200px w-xs-150px">
                  <span className="cursor-pointer">{t('OPERATIONAL_CP.ROW_SECTION.ASSIGNEE')}</span>
                </th>
                {!isSupplierLoggedIn && (OperationalAddEditAccess || OperationalDeleteAccess) && (
                  <th className="w-150px">
                    <span className="cursor-pointer">{t('OPERATIONAL_CP.ROW_SECTION.ACTION')}</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              <DndProvider backend={HTML5Backend}>
                {editState?.Rows && !isEmpty(editState?.Rows) ? (
                  editState?.Rows?.map((rowItem, rowIndex) => {
                    return <React.Fragment key={`${rowItem?._id}_rowInd${rowIndex}`}>{renderTableRow(rowItem, rowIndex)}</React.Fragment>;
                  })
                ) : (
                  <tr>
                    <td className=" text-center" colSpan="100%">
                      <span>{t('OPERATIONAL_CP.ROW_SECTION.NO_ROW_ADDED')}</span>
                    </td>
                  </tr>
                )}
              </DndProvider>
            </tbody>
          </table>
        </div>
      </div>
      {editState.showRowModal && (
        <AddEditRow
          isProductArchived={isProductArchived}
          open={editState.showRowModal}
          toggle={closeRowModal}
          editData={editState.editRowData}
          addEditState={editState}
        />
      )}
    </>
  );
}

export default RowSection;
